import { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { loadJson } from '../utils/apiRequest';
import toast from 'react-hot-toast';
import { renderPage } from '../grapesjs/handwerk-blocks';
import { aufTaste } from '../utils/tastaturBedienung';

function enrichBlocksWithKiContent(blocks, kiPage) {
  return blocks.map(block => {
    const b = { ...block, data: { ...block.data } };
    if (/hero/.test(block.type)) {
      if (kiPage.ki_h1)        b.data.headline = kiPage.ki_h1;
      if (kiPage.ki_hero_text) b.data.subline  = kiPage.ki_hero_text;
      if (kiPage.ki_cta)       b.data.cta_text = kiPage.ki_cta;
    } else if (block.type === 'ueber-uns' && kiPage.ki_abschnitt_text) {
      b.data.text = kiPage.ki_abschnitt_text;
    }
    return b;
  });
}

const TEMPLATE_PRESETS = [
  { id: 'modern-clean',   name: 'Modern Clean',    desc: 'Minimalistisch, viel Weissraum, klare Typografie',    icon: '⬜', style: { borderRadius: 8, shadows: 'leicht', density: 'luftig' },   preview_gradient: 'linear-gradient(135deg, #f8fafc, #e2e8f0)' },
  { id: 'handwerk-bold',  name: 'Handwerk Bold',   desc: 'Kraftvoll, erdige Toene, handwerkliches Gefuehl',     icon: '🔨', style: { borderRadius: 4, shadows: 'mittel', density: 'kompakt' },  preview_gradient: 'linear-gradient(135deg, #292524, #57534e)' },
  { id: 'service-trust',  name: 'Service & Trust',  desc: 'Vertrauenswuerdig, professionell, klar strukturiert', icon: '🤝', style: { borderRadius: 6, shadows: 'mittel', density: 'normal' },   preview_gradient: 'linear-gradient(135deg, #1e3a5f, #185fa5)' },
  { id: 'local-friendly', name: 'Local Friendly',   desc: 'Warm, freundlich, lokal verwurzelt',                  icon: '🏘️', style: { borderRadius: 12, shadows: 'leicht', density: 'normal' },  preview_gradient: 'linear-gradient(135deg, #065f46, #059669)' },
  { id: 'premium-dark',   name: 'Premium Dark',     desc: 'Hochwertig, dunkel, exklusives Auftreten',            icon: '\u2726', style: { borderRadius: 4, shadows: 'stark', density: 'luftig' }, preview_gradient: 'linear-gradient(135deg, #0f172a, #1e293b)' },
];

export default function DesignStudio({ project, leadId, token, brandData, sitemapPages }) {
  const [step, setStep]                         = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dbTemplates, setDbTemplates]           = useState([]);
  const [selectedPage, setSelectedPage]         = useState(null);
  const [generating, setGenerating]             = useState(false);
  const [designResult, setDesignResult]         = useState(null);
  const [designBlocks, setDesignBlocks]         = useState([]);
  const [designBrand, setDesignBrand]           = useState(null);
  const [linkReport, setLinkReport]             = useState([]);
  const [linkSummary, setLinkSummary]           = useState(null);
  const [kiInjected, setKiInjected]             = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadJson(`${API_BASE_URL}/api/templates/`, { headers }, { context: 'Vorlagen', fallback: [] })
      .then(data => setDbTemplates(Array.isArray(data) ? data.slice(0, 6) : []));
  }, []); // eslint-disable-line

  const primaryColor   = brandData?.primary_color   || '#008EAA';
  const secondaryColor = brandData?.secondary_color || '#004F59';
  const fontPrimary    = brandData?.font_primary    || 'Inter';

  const generateDesign = async () => {
    if (!selectedPage) { toast.error('Bitte Seite waehlen'); return; }
    setGenerating(true);
    try {
      // 1. Block-JSON von Claude holen
      const res = await fetch(
        `${API_BASE_URL}/api/projects/${project.id}/design-json/${selectedPage.id}`,
        { method: 'POST', headers }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Generierung fehlgeschlagen');
      }
      const { blocks: rawBlocks, brand, ki_injected } = await res.json();

      // Client-side enrichment as safety net if ki_* content was generated
      const kiPage = (sitemapPages || []).find(p => p.id === selectedPage.id);
      const blocks = kiPage?.content_generated
        ? enrichBlocksWithKiContent(rawBlocks, kiPage)
        : rawBlocks;
      setKiInjected(ki_injected || (kiPage?.content_generated ?? false));

      // 2. HTML aus Block-Bibliothek rendern
      const html = renderPage(blocks, brand);

      // 3. Link-Resolver
      const linkRes = await fetch(
        `${API_BASE_URL}/api/projects/${project.id}/resolve-links`,
        { method: 'POST', headers, body: JSON.stringify({ html, page_id: selectedPage.id }) }
      );
      const linkData = linkRes.ok ? await linkRes.json() : { html, link_report: [], summary: {} };

      // 4. State setzen
      setDesignBlocks(blocks);
      setDesignBrand(brand);
      setDesignResult(linkData.html);
      setLinkReport(linkData.link_report || []);
      setLinkSummary(linkData.summary || {});
      setStep(4);
      toast.success(
        ki_injected || kiPage?.content_generated
          ? `${blocks.length} Bloecke generiert · KI-Texte eingesetzt`
          : `${blocks.length} Bloecke generiert`
      );
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Schritt-Anzeige */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-light)', background: 'var(--bg-app)', padding: '12px 20px', gap: 0 }}>
        {[
          { n: 1, label: 'Template waehlen' },
          { n: 2, label: 'Brand anwenden' },
          { n: 3, label: 'Seite waehlen' },
          { n: 4, label: 'KI generiert' },
        ].map((s, i) => (
          <div key={s.n} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {i > 0 && <div style={{ width: 24, height: 2, background: step > i ? 'var(--brand-primary)' : 'var(--border-light)' }} />}
            <div role="button" tabIndex={0} onKeyDown={aufTaste(() => step > s.n && setStep(s.n))} onClick={() => step > s.n && setStep(s.n)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: step > s.n ? 'pointer' : 'default' }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: step >= s.n ? 'var(--brand-primary)' : 'var(--bg-elevated)',
                color: step >= s.n ? '#fff' : 'var(--text-tertiary)',
                fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {step > s.n ? '\u2713' : s.n}
              </div>
              <span style={{ fontSize: 12, fontWeight: step === s.n ? 700 : 400, color: step >= s.n ? 'var(--text-primary)' : 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '20px 24px' }}>

        {/* Schritt 1: Template waehlen */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Template waehlen</div>

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Stil-Vorlagen</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
              {TEMPLATE_PRESETS.map(t => (
                <div role="button" tabIndex={0} onKeyDown={aufTaste(() => { setSelectedTemplate(t); setStep(2); })}
                  key={t.id}
                  onClick={() => { setSelectedTemplate(t); setStep(2); }}
                  style={{
                    borderRadius: 10, overflow: 'hidden',
                    border: `2px solid ${selectedTemplate?.id === t.id ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{ height: 80, background: t.preview_gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{t.icon}</div>
                  <div style={{ padding: '10px 12px', background: 'var(--bg-surface)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {dbTemplates.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Gespeicherte Templates</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  {dbTemplates.map(t => (
                    <div role="button" tabIndex={0} onKeyDown={aufTaste(() => { setSelectedTemplate(t); setStep(2); })} key={t.id} onClick={() => { setSelectedTemplate(t); setStep(2); }}
                      style={{ borderRadius: 10, overflow: 'hidden', border: `2px solid ${selectedTemplate?.id === t.id ? 'var(--brand-primary)' : 'var(--border-light)'}`, cursor: 'pointer' }}>
                      {t.thumbnail_url ? (
                        <img src={t.thumbnail_url} alt={t.name} style={{ width: '100%', height: 80, objectFit: 'cover' }} />
                      ) : (
                        <div style={{ height: 80, background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🌐</div>
                      )}
                      <div style={{ padding: '10px 12px', background: 'var(--bg-surface)' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{t.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Schritt 2: Brand anwenden */}
        {step === 2 && selectedTemplate && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
              Brand Design auf "{selectedTemplate.name}" anwenden
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
              {/* Brand-Daten */}
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)', borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Erkannte Brand-Daten</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {[primaryColor, secondaryColor].filter(Boolean).map((c, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 6, background: c, border: '1px solid var(--border-light)' }} />
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{c}</div>
                    </div>
                  ))}
                </div>
                {fontPrimary && (
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: primaryColor }}>{fontPrimary}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Aa Bb Cc 1 2 3</div>
                  </div>
                )}
                {!brandData?.primary_color && (
                  <div style={{ fontSize: 12, color: 'var(--status-warning-text)', background: 'var(--status-warning-bg)', padding: '8px 10px', borderRadius: 6, marginTop: 8 }}>
                    Noch kein Brand-Scan — Standardfarben werden verwendet.
                  </div>
                )}
              </div>

              {/* Template-Vorschau */}
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ height: 8, background: primaryColor }} />
                <div style={{ padding: 16 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: primaryColor, marginBottom: 6 }}>
                    {project?.company_name || 'Ihr Unternehmen'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>{selectedTemplate.desc}</div>
                  <div style={{ display: 'inline-block', padding: '6px 14px', background: primaryColor, color: '#fff', borderRadius: selectedTemplate.style?.borderRadius || 6, fontSize: 12, fontWeight: 600 }}>
                    Kontakt aufnehmen
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                Anderes Template
              </button>
              <button onClick={() => setStep(3)} style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: 'var(--brand-primary)', color: 'var(--text-on-brand)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                Brand passt — Seite waehlen
              </button>
            </div>
          </div>
        )}

        {/* Schritt 3: Seite waehlen */}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Fuer welche Seite soll der Entwurf erstellt werden?</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 20 }}>
              {sitemapPages.map(page => (
                <div role="button" tabIndex={0} onKeyDown={aufTaste(() => setSelectedPage(page))} key={page.id} onClick={() => setSelectedPage(page)}
                  style={{
                    padding: '12px 14px', borderRadius: 8,
                    border: `2px solid ${selectedPage?.id === page.id ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                    background: selectedPage?.id === page.id ? 'var(--bg-active, var(--bg-elevated))' : 'var(--bg-surface)',
                    cursor: 'pointer',
                  }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: selectedPage?.id === page.id ? 'var(--brand-primary)' : 'var(--text-primary)', marginBottom: 3 }}>{page.page_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{page.page_type}</div>
                </div>
              ))}
            </div>
            {sitemapPages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-tertiary)', fontSize: 13 }}>
                Noch keine Sitemap-Seiten angelegt. Bitte zuerst im Analyse-Tab die Seitenstruktur definieren.
              </div>
            )}
            <button
              onClick={generateDesign}
              disabled={!selectedPage || generating}
              style={{
                padding: '11px 28px', borderRadius: 8, border: 'none',
                background: !selectedPage || generating ? 'var(--border-medium)' : 'linear-gradient(135deg, #008EAA, #006680)',
                color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: !selectedPage || generating ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: !selectedPage || generating ? 'none' : '0 2px 10px var(--kc-mid-a-30)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              {generating ? (
                <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .8s linear infinite', display: 'inline-block' }} />KI erstellt Entwurf...</>
              ) : 'Design jetzt generieren'}
            </button>
          </div>
        )}

        {/* Schritt 4: Ergebnis */}
        {step === 4 && designResult && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Design-Entwurf: {selectedPage?.page_name}
                </div>
                {kiInjected && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6, background: 'var(--kc-mid-a-12)', border: '1px solid var(--kc-mid-a-25)' }}>
                    <span style={{ fontSize: 12, color: 'var(--brand-primary-mid)', fontWeight: 700 }}>KI-Texte eingesetzt</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setStep(3)} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                  Neue Variante
                </button>
                <button
                  onClick={() => {
                    const page = sitemapPages.find(p => p.id === selectedPage.id);
                    if (page) window.dispatchEvent(new CustomEvent('kompagnon:open-editor', { detail: { pageId: page.id, html: designResult, blocks: designBlocks, brand: designBrand } }));
                  }}
                  style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--brand-primary)', color: 'var(--text-on-brand)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
                >
                  Im Editor bearbeiten
                </button>
              </div>
            </div>
            <div style={{ border: '1px solid var(--border-light)', borderRadius: 10, overflow: 'hidden', height: 480 }}>
              <iframe
                srcDoc={typeof designResult === 'string' ? designResult : JSON.stringify(designResult)}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Design-Vorschau"
              />
            </div>

            {/* Link-Validator */}
            {linkReport.length > 0 && (
              <div style={{ marginTop: 16, background: 'var(--bg-surface)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Link-Check</div>
                  {linkSummary && (
                    <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                      <span style={{ color: 'var(--status-success-text)', fontWeight: 600 }}>{linkSummary.ok} OK</span>
                      {linkSummary.auto_fixed > 0 && <span style={{ color: 'var(--brand-primary-mid)', fontWeight: 600 }}>{linkSummary.auto_fixed} korrigiert</span>}
                      {linkSummary.unresolved > 0 && <span style={{ color: 'var(--status-warning-text)', fontWeight: 600 }}>{linkSummary.unresolved} offen</span>}
                    </div>
                  )}
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 90px', gap: 0, padding: '8px 18px', background: 'var(--bg-app)', fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0 }}>
                    <div>Link-Text</div><div>Ziel-URL</div><div>Status</div>
                  </div>
                  {linkReport.map((link, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 90px', gap: 0, padding: '9px 18px', borderBottom: '1px solid var(--border-light)', background: link.status === 'unresolved' ? 'var(--status-warning-bg)' : 'transparent' }}>
                      <div style={{ fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginRight: 6 }}>{link.tag}</span>{link.text || '(kein Text)'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link.href || '\u2014'}</div>
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                          background: link.status === 'ok' ? 'var(--status-success-bg)' : link.status === 'auto_fixed' ? 'var(--kc-mid-a-12)' : 'var(--status-warning-bg)',
                          color: link.status === 'ok' ? 'var(--status-success-text)' : link.status === 'auto_fixed' ? 'var(--brand-primary)' : 'var(--status-warning-text)',
                        }}>
                          {link.status === 'ok' ? 'OK' : link.status === 'auto_fixed' ? 'Korrigiert' : 'Offen'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {linkSummary?.unresolved > 0 && (
                  <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border-light)', fontSize: 12, color: 'var(--status-warning-text)', background: 'var(--status-warning-bg)' }}>
                    {linkSummary.unresolved} Links nicht automatisch aufloesbar — manuell im Editor pruefen.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
